import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useAppDispatch } from 'store/hooks'
import { offeringUpdateThunk } from 'store/reducers/temple/offeringSlice'
import { IOfferingResponse } from 'types/api/temple'
import { extractError } from 'utils/api'

type BulkOfferingCourierState = {
    isCourierAvailable: boolean
}

interface BulkOfferingCourierDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    onClose: () => void
    onComplete?: () => void
}

const getInitialState = (offering?: IOfferingResponse): BulkOfferingCourierState => ({
    isCourierAvailable: offering?.isCourierAvailable ?? false
})

const BulkOfferingCourierDialog: React.FC<BulkOfferingCourierDialogProps> = ({ open, offerings, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [values, setValues] = useState<BulkOfferingCourierState>(getInitialState())
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (open) {
            setValues(getInitialState(offerings[0]))
        }
    }, [open, offerings])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues({ isCourierAvailable: event.target.checked })
    }

    const handleSave = async () => {
        if (offerings.length === 0) return

        setSubmitting(true)
        try {
            await Promise.all(
                offerings.map(offering =>
                    dispatch(offeringUpdateThunk({
                        url: offering.url,
                        data: values
                    })).then(unwrapResult)
                )
            )
            enqueueSnackbar('Updated selected offerings', { variant: 'success' })
            onComplete?.()
            onClose()
        } catch (error) {
            enqueueSnackbar(extractError(error) ?? 'Failed to update selected offerings', { variant: 'error' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Bulk Courier Availability</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        Apply courier availability to {offerings.length} selected offering{offerings.length === 1 ? '' : 's'}.
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                    <FormControlLabel
                        control={<Switch checked={values.isCourierAvailable} onChange={handleChange} />}
                        label="Courier Available"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined" disabled={submitting}>
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={submitting || offerings.length === 0}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default BulkOfferingCourierDialog
